import { createSlice } from "@reduxjs/toolkit";
import { SeatStatus } from "./enums/SeatStatus";
import { MemberSeat } from "./models/MemberSeat";
import { SeatRequest } from "./models/SeatRequest";
import {
  acceptSeat,
  declineSeat,
  fetchSeatRequests,
  fetchSessionSeats,
} from "./slice-extensions/seats-slice-extension";
import { fetchMemberUpcomingSeats } from "./slice-extensions/occupancy-slice-extension";

interface SeatsState {
  /** Open booking requests, soonest date first. */
  requests: SeatRequest[];
  requestsTotal: number;
  requestsStatus: "idle" | "loading" | "succeeded" | "failed";
  /** A Session's booked dates (both statuses), per session id. */
  seatsBySession: Record<string, SeatRequest[]>;
  /** A member's upcoming booked single dates, per member id. */
  upcomingByMember: Record<string, MemberSeat[]>;
}

const initialState: SeatsState = {
  requests: [],
  requestsTotal: 0,
  requestsStatus: "idle",
  seatsBySession: {},
  upcomingByMember: {},
};

const seatsSlice = createSlice({
  name: "seats",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSeatRequests.pending, (state) => {
        state.requestsStatus = "loading";
      })
      .addCase(fetchSeatRequests.fulfilled, (state, action) => {
        state.requests = action.payload.items;
        state.requestsTotal = action.payload.totalCount;
        state.requestsStatus = "succeeded";
      })
      .addCase(fetchSeatRequests.rejected, (state) => {
        state.requestsStatus = "failed";
      })
      .addCase(fetchSessionSeats.fulfilled, (state, action) => {
        state.seatsBySession[action.payload.bookingId] = action.payload.seats;
      })
      .addCase(fetchMemberUpcomingSeats.fulfilled, (state, action) => {
        state.upcomingByMember[action.meta.arg] = action.payload;
      })
      .addCase(acceptSeat.fulfilled, (state, action) => {
        const { bookingId, id } = action.payload;
        removeRequest(state, id);
        state.seatsBySession[bookingId] = (state.seatsBySession[bookingId] ?? []).map((s) =>
          s.seatId === id ? { ...s, status: SeatStatus.Confirmed, isPending: false } : s,
        );
      })
      .addCase(declineSeat.fulfilled, (state, action) => {
        const { bookingId, seatId } = action.payload;
        removeRequest(state, seatId);
        state.seatsBySession[bookingId] = (state.seatsBySession[bookingId] ?? []).filter(
          (s) => s.seatId !== seatId,
        );
      });
  },
});

function removeRequest(state: SeatsState, seatId: string): void {
  const before = state.requests.length;
  state.requests = state.requests.filter((r) => r.seatId !== seatId);
  state.requestsTotal = Math.max(0, state.requestsTotal - (before - state.requests.length));
}

export const selectSeatRequests = (state: { seats: SeatsState }): SeatRequest[] => state.seats.requests;

export const selectSeatRequestsTotal = (state: { seats: SeatsState }): number => state.seats.requestsTotal;

export const selectSeatRequestsStatus = (state: { seats: SeatsState }): SeatsState["requestsStatus"] =>
  state.seats.requestsStatus;

const NO_SEATS: SeatRequest[] = [];

export const selectSessionSeats = (sessionId: string) =>
  (state: { seats: SeatsState }): SeatRequest[] => state.seats.seatsBySession[sessionId] ?? NO_SEATS;

const NO_MEMBER_SEATS: MemberSeat[] = [];

export const selectMemberUpcomingSeats = (memberId: string) =>
  (state: { seats: SeatsState }): MemberSeat[] => state.seats.upcomingByMember[memberId] ?? NO_MEMBER_SEATS;

export default seatsSlice.reducer;
